import { useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { EUserRoleName, GC_PROJECT_ADMIN_ROLES } from "constants/index";

const ALL_ROLES = [
  EUserRoleName[EUserRoleName.gc_project_admin],
  EUserRoleName[EUserRoleName.gc_estimator],
  EUserRoleName[EUserRoleName.gc_foreman],
  EUserRoleName[EUserRoleName.gc_sc_project_admin],
  EUserRoleName[EUserRoleName.gc_sc_foreman],
  EUserRoleName[EUserRoleName.gc_sc_architect],
  EUserRoleName[EUserRoleName.gc_sc_design_engineer],
  EUserRoleName[EUserRoleName.gc_sc_owner]
];

const GC_PROJECT_ADMIN = [...GC_PROJECT_ADMIN_ROLES];

const GC_PROJECT_ADMIN_FOREMAN = [
  ...GC_PROJECT_ADMIN_ROLES,
  EUserRoleName[EUserRoleName.gc_foreman]
];

const PAGE_ROLE_MAPPING: Record<string, string[]> = {
  dashboard: ALL_ROLES,
  "analytics/export": ALL_ROLES,
  "bid-packages": GC_PROJECT_ADMIN_FOREMAN,
  "bid-packages/:bidPackageId": GC_PROJECT_ADMIN_FOREMAN,
  "design-packages": GC_PROJECT_ADMIN_FOREMAN,
  "design-packages/:designPackageId": GC_PROJECT_ADMIN_FOREMAN,
  "design-packages/:designPackageId/details": GC_PROJECT_ADMIN_FOREMAN,
  "design-scope": GC_PROJECT_ADMIN_FOREMAN,
  settings: ALL_ROLES,
  "settings/modules": GC_PROJECT_ADMIN_FOREMAN,
  "settings/locations": GC_PROJECT_ADMIN_FOREMAN,
  "settings/integrations": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general": ALL_ROLES,
  "settings/general/project-users": ALL_ROLES,
  "settings/general/project-users/permissions": ALL_ROLES,
  "settings/general/project-companies": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/details": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/calendar": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/distribution-groups": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages/rename-fields": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages/templates": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-scope": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-scope/level-of-detail": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-scope/rename-fields": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/rename-fields": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/templates": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/actions": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/rename-field": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/numbering": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/dateblock": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/rename-field": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/templates": GC_PROJECT_ADMIN_FOREMAN,
  Submittals: ALL_ROLES,
  submittals: ALL_ROLES,
  "submittals-untracked": ALL_ROLES,
  "submittals-voided": ALL_ROLES,
  "submittals-deleted": ALL_ROLES,
  "submittals/:submittalId": ALL_ROLES,
  Materials: ALL_ROLES,
  materials: ALL_ROLES,
  "materials/:materialId": ALL_ROLES,
  "materials/extracted": GC_PROJECT_ADMIN_FOREMAN,
  schedule: ALL_ROLES,
  "schedule/submittal-linking": GC_PROJECT_ADMIN_FOREMAN,
  "schedule/material-linking": GC_PROJECT_ADMIN_FOREMAN,
  "schedule/change-impact": GC_PROJECT_ADMIN,
  "schedule/procurement-schedule": ALL_ROLES,
  "schedule/project-schedules": ALL_ROLES,
  "project-schedule/:selectedVersionId": ALL_ROLES,
  reports: GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports": GC_PROJECT_ADMIN_FOREMAN,
  "reports/audit-reports": GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports/risk": GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports/upcoming": GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports/activities": GC_PROJECT_ADMIN_FOREMAN,
  "reports/audit-reports/activity-log": GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports/custom-report/:reportId": GC_PROJECT_ADMIN_FOREMAN,
  "reports/project-reports/procurement-follow-ups": GC_PROJECT_ADMIN_FOREMAN,
  printView: ALL_ROLES,
  "custom-excel-export": ALL_ROLES
};

const VALID_ROUTE_PATTERNS = [
  // Public routes
  { path: "/", exact: true },
  { path: "/Login", exact: true },
  { path: "/callback", exact: true },
  { path: "/subscription-select", exact: true },
  { path: "/set_password", exact: true },
  { path: "/reset_password", exact: true },
  { path: "/forgot_password", exact: true },
  { path: "/reset_expired_password", exact: true },
  { path: "/accept-invite", exact: true },
  { path: "/submit-lead-time", exact: true },
  { path: "/admin", exact: true },

  // Private routes - Home level
  { path: "/home", exact: true },
  { path: "/new-project", exact: true },
  { path: "/change_password", exact: true },
  { path: "/users", exact: true },
  { path: "/companies", exact: true },
  { path: "/projects", exact: true },
  { path: "/analytics", exact: true },
  { path: "/analytics/export", exact: true },
  { path: "/reports", exact: true },
  { path: "/reports/audit-reports", exact: true },
  { path: "/reports/audit-reports/activity-log", exact: true },
  { path: "/reports/printView", exact: true },
  { path: "/settings", exact: true },
  { path: "/settings/:tab1Id", exact: true },
  { path: "/settings/:tab1Id/:tab2Id", exact: true },
  { path: "/settings/:tab1Id/:tab2Id/:tab3Id", exact: true },

  // Private routes - Project level
  { path: "/project/:projectId/dashboard", exact: true },
  { path: "/project/:projectId/design", exact: true },
  { path: "/project/:projectId/submittals", exact: true },
  { path: "/project/:projectId/submittals-untracked", exact: true },
  { path: "/project/:projectId/submittals-voided", exact: true },
  { path: "/project/:projectId/submittals-deleted", exact: true },
  { path: "/project/:projectId/submittals/:submittalId", exact: true },
  { path: "/project/:projectId/Submittals/:submittalId", exact: true },
  { path: "/project/:projectId/bid-packages", exact: true },
  { path: "/project/:projectId/bid-packages/:bidPackageId", exact: true },
  { path: "/project/:projectId/design-packages", exact: true },
  { path: "/project/:projectId/design-packages/:designPackageId", exact: true },
  {
    path: "/project/:projectId/design-packages/:designPackageId/details",
    exact: true
  },
  { path: "/project/:projectId/design-scope", exact: true },
  { path: "/project/:projectId/settings", exact: true },
  { path: "/project/:projectId/settings/:tab1Id", exact: true },
  { path: "/project/:projectId/settings/:tab1Id/:tab2Id", exact: true },
  { path: "/project/:projectId/settings/:tab1Id/:tab2Id/:tab3Id", exact: true },
  { path: "/project/:projectId/materials", exact: true },
  { path: "/project/:projectId/materials/extracted", exact: true },
  { path: "/project/:projectId/materials/:materialId", exact: true },
  { path: "/project/:projectId/Materials/:materialId", exact: true },
  { path: "/project/:projectId/schedule", exact: true },
  { path: "/project/:projectId/schedule/project-schedules", exact: true },
  { path: "/project/:projectId/schedule/project-schedules/opc", exact: true },
  {
    path: "/project/:projectId/schedule/project-schedule/:selectedVersionId",
    exact: true
  },
  { path: "/project/:projectId/printView" },
  { path: "/project/:projectId/custom-excel-export" },
  { path: "/project/:projectId/reports", exact: true },
  {
    path: "/project/:projectId/reports/project-reports",
    exact: true
  },
  {
    path: "/project/:projectId/reports/audit-reports",
    exact: true
  },
  { path: "/project/:projectId/reports/project-reports/risk", exact: true },
  { path: "/project/:projectId/reports/project-reports/upcoming", exact: true },
  {
    path: "/project/:projectId/reports/project-reports/activities",
    exact: true
  },
  {
    path: "/project/:projectId/reports/audit-reports/activity-log",
    exact: true
  },
  {
    path: "/project/:projectId/reports/project-reports/procurement-follow-ups",
    exact: true
  },
  {
    path: "/project/:projectId/reports/project-reports/custom-report/:reportId",
    exact: true
  },
  { path: "/project/:projectId/reports/submittals/export", exact: true },
  { path: "/project/:projectId/analytics/export", exact: true },
  { path: "/project/:projectId/schedule/change-impact", exact: true },
  { path: "/project/:projectId/schedule/submittal-linking", exact: true },
  { path: "/project/:projectId/schedule/material-linking", exact: true }
];

/**
 * Details page patterns that should bypass role checking
 * These are detail pages that inherit permissions from their parent pages
 */
const DETAILS_PAGE_PATTERNS = [
  /\/project\/[^/]+\/submittals\/[^/]+$/,
  /\/project\/[^/]+\/bid-packages\/[^/]+$/,
  /\/project\/[^/]+\/design-packages\/[^/]+$/,
  /\/project\/[^/]+\/design-packages\/[^/]+\/details$/,
  /\/project\/[^/]+\/materials\/[^/]+$/,
  /\/project\/[^/]+\/schedule\/project-schedule\/[^/]+$/,
  /\/project\/[^/]+\/reports\/project-reports\/custom-report\/[^/]+$/
];

type ProjectRouteParts = {
  pageName: string;
  subPage?: string;
  fourthPart?: string;
  fifthPart?: string;
  isDetails: boolean;
};

const SCHEDULE_SUB_PAGE_KEYS: Record<string, string> = {
  "project-schedules": "schedule/project-schedules",
  "change-impact": "schedule/change-impact",
  "submittal-linking": "schedule/submittal-linking",
  "material-linking": "schedule/material-linking",
  "procurement-schedule": "schedule/procurement-schedule"
};

const parseProjectRoute = (
  pathname: string,
  parts: string[]
): ProjectRouteParts | null => {
  if (parts[0] !== "project" || parts.length < 3) {
    return null;
  }

  return {
    pageName: parts[2],
    subPage: parts[3],
    fourthPart: parts[4],
    fifthPart: parts[5],
    isDetails: DETAILS_PAGE_PATTERNS.some((pattern) => pattern.test(pathname))
  };
};

const isDetailPage = (route: ProjectRouteParts) =>
  route.isDetails && !!route.subPage && !route.fourthPart;

const getDetailPageKey = (
  baseKey: string,
  detailKey: string,
  route: ProjectRouteParts
) => (isDetailPage(route) ? detailKey : baseKey);

// The details page sits one segment deeper than the other logs', which
// isDetailPage rejects as a fourth part.
const getDesignPackagesPageKey = (route: ProjectRouteParts) => {
  if (route.isDetails && route.subPage && route.fourthPart === "details") {
    return "design-packages/:designPackageId/details";
  }

  return getDetailPageKey(
    "design-packages",
    "design-packages/:designPackageId",
    route
  );
};

const getSchedulePageKey = (route: ProjectRouteParts) => {
  if (route.subPage === "project-schedule" && route.fourthPart) {
    return "project-schedule/:selectedVersionId";
  }

  if (route.subPage && SCHEDULE_SUB_PAGE_KEYS[route.subPage]) {
    return SCHEDULE_SUB_PAGE_KEYS[route.subPage];
  }

  return "schedule";
};

const getReportsPageKey = (route: ProjectRouteParts) => {
  if (
    route.subPage === "project-reports" &&
    route.fourthPart === "custom-report" &&
    route.fifthPart
  ) {
    return "reports/project-reports/custom-report/:reportId";
  }

  if (
    (route.subPage === "project-reports" ||
      route.subPage === "audit-reports") &&
    route.fourthPart
  ) {
    return `reports/${route.subPage}/${route.fourthPart}`;
  }

  if (route.subPage) {
    return `reports/${route.subPage}`;
  }

  return "reports";
};

const getSettingsPageKey = (route: ProjectRouteParts) => {
  const { pageName, subPage, fourthPart, fifthPart } = route;

  if (subPage && fourthPart && fifthPart) {
    return `${pageName}/${subPage}/${fourthPart}/${fifthPart}`;
  }

  if (subPage && fourthPart) {
    return `${pageName}/${subPage}/${fourthPart}`;
  }

  if (subPage) {
    return `${pageName}/${subPage}`;
  }

  return pageName;
};

const getMaterialsPageKey = (route: ProjectRouteParts) => {
  if (route.subPage === "extracted") {
    return "materials/extracted";
  }

  return getDetailPageKey("materials", "materials/:materialId", route);
};

const PAGE_KEY_HANDLERS: Record<string, (route: ProjectRouteParts) => string> =
  {
    "bid-packages": (route) =>
      getDetailPageKey("bid-packages", "bid-packages/:bidPackageId", route),
    "design-packages": getDesignPackagesPageKey,
    submittals: (route) =>
      getDetailPageKey("submittals", "submittals/:submittalId", route),
    "submittals-untracked": () => "submittals-untracked",
    "submittals-voided": () => "submittals-voided",
    "submittals-deleted": () => "submittals-deleted",
    materials: getMaterialsPageKey,
    schedule: getSchedulePageKey,
    reports: getReportsPageKey,
    settings: getSettingsPageKey
  };

function extractPageKey(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  const projectRoute = parseProjectRoute(pathname, parts);

  if (!projectRoute) {
    // Unreachable `|| ""` arm: extractPageKey is only called for pathnames
    // starting with "/project/", and `parts` is filter(Boolean)-ed, so the
    // array is always non-empty and its last entry is always a truthy string.
    /* v8 ignore next */
    return parts[parts.length - 1] || "";
  }

  const handler = PAGE_KEY_HANDLERS[projectRoute.pageName];
  return handler ? handler(projectRoute) : projectRoute.pageName;
}

function useValidateRouteAccess({
  userRole
}: {
  userRole: string | undefined;
}): boolean {
  const { pathname } = useLocation();

  return useMemo(() => {
    // Check if pathname matches any valid route pattern
    const matchesValidRoute = VALID_ROUTE_PATTERNS.some((route) => {
      const match = matchPath(pathname, {
        path: route.path,
        exact: route.exact,
        strict: false
      });
      return match !== null;
    });

    // If route doesn't match any valid pattern, it's invalid
    if (!matchesValidRoute) {
      return false;
    }

    // For project routes, check role permissions
    if (pathname.startsWith("/project/")) {
      // If no user role is set, deny access
      if (!userRole) {
        return false;
      }

      // Extract page key for role checking
      const pageKey = extractPageKey(pathname);
      const allowedRoles = PAGE_ROLE_MAPPING[pageKey];

      // If no role mapping exists for this page, deny access
      if (!allowedRoles) {
        return false;
      }

      // Check if user's role is in the allowed roles list
      return allowedRoles.includes(userRole);
    }

    return true;
  }, [pathname, userRole]);
}

export default useValidateRouteAccess;
